import React from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import {
  Alert,
  Box,
  Button,
  Collapse,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Iconify from "../../iconify/Iconify";

const buildSessionId = () =>
  `session-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const createEmptySession = () => ({
  sessionId: buildSessionId(),
  label: "",
  dateStart: "",
  dateEnd: "",
  maxParticipants: "",
});

const GERMAN_WEEKDAYS = [
  "Sonntag",
  "Montag",
  "Dienstag",
  "Mittwoch",
  "Donnerstag",
  "Freitag",
  "Samstag",
];

const MAX_DAILY_SESSIONS = 60;

const TEMPLATE_OPTIONS = [
  {
    value: "daily_sessions",
    label: "Tagessessions",
    description:
      "Pro Tag eine Session vom Event-Start bis Event-Ende. Ideal für Ferienbetreuung.",
    needsConfig: true,
  },
  {
    value: "multi_day_camp",
    label: "Mehrtages-Camp",
    description: "Erzeugt eine konfigurierbare Anzahl aufeinanderfolgender Sessions ab Event-Beginn.",
    needsConfig: true,
  },
  {
    value: "two_weekend_blocks",
    label: "2 Wochenend-Blöcke",
    description: "Erzeugt 2 Sessions an aufeinanderfolgenden Wochenenden.",
    needsConfig: false,
  },
];

const toDateTimeLocal = (value) => {
  if (!value) return "";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DDTHH:mm") : "";
};

const formatGermanDate = (dayjsDate) => {
  const weekday = GERMAN_WEEKDAYS[dayjsDate.day()];
  return `${weekday} ${dayjsDate.format("DD.MM.")}`;
};

const buildTemplateSessions = ({
  templateValue,
  eventDateBegin,
  eventDateEnd,
  templateConfig,
}) => {
  const baseStart = dayjs(eventDateBegin).isValid() ? dayjs(eventDateBegin) : null;
  const baseEnd = dayjs(eventDateEnd).isValid() ? dayjs(eventDateEnd) : null;

  if (templateValue === "daily_sessions") {
    if (!baseStart || !baseEnd) return [];
    const totalDays = baseEnd.startOf("day").diff(baseStart.startOf("day"), "day") + 1;
    if (totalDays <= 0 || totalDays > MAX_DAILY_SESSIONS) return [];

    const startTime = templateConfig?.startTime || "08:00";
    const endTime = templateConfig?.endTime || "13:00";

    return Array.from({ length: totalDays }, (_, index) => {
      const day = baseStart.startOf("day").add(index, "day");
      const start = day.format("YYYY-MM-DD") + "T" + startTime;
      const end = day.format("YYYY-MM-DD") + "T" + endTime;
      return {
        ...createEmptySession(),
        label: formatGermanDate(day),
        dateStart: start,
        dateEnd: end,
      };
    });
  }

  if (templateValue === "multi_day_camp") {
    const days = Math.max(1, Math.min(MAX_DAILY_SESSIONS, Number(templateConfig?.days) || 3));
    const start = baseStart || dayjs();
    const end = baseEnd || start.add(2, "hour");

    return Array.from({ length: days }, (_, index) => ({
      ...createEmptySession(),
      label: `Tag ${index + 1}`,
      dateStart: toDateTimeLocal(start.add(index, "day")),
      dateEnd: toDateTimeLocal(end.add(index, "day")),
    }));
  }

  if (templateValue === "two_weekend_blocks") {
    const start = baseStart || dayjs();
    const end = baseEnd || start.add(2, "hour");

    return [0, 7].map((offset, idx) => ({
      ...createEmptySession(),
      label: `Wochenend-Block ${idx + 1}`,
      dateStart: toDateTimeLocal(start.add(offset, "day")),
      dateEnd: toDateTimeLocal(end.add(offset, "day")),
    }));
  }

  return [];
};

const getDailySessionsPreview = (eventDateBegin, eventDateEnd) => {
  const start = dayjs(eventDateBegin);
  const end = dayjs(eventDateEnd);
  if (!start.isValid() || !end.isValid()) return null;

  const totalDays = end.startOf("day").diff(start.startOf("day"), "day") + 1;
  if (totalDays <= 0) return null;
  if (totalDays > MAX_DAILY_SESSIONS) {
    return { error: `Zeitraum zu groß (max. ${MAX_DAILY_SESSIONS} Tage).` };
  }

  return {
    count: totalDays,
    range: `${start.format("DD.MM.YYYY")} \u2013 ${end.format("DD.MM.YYYY")}`,
  };
};

export default function StepSessions({ formData, errors, onChange }) {
  const sessions = Array.isArray(formData.sessions) ? formData.sessions : [];
  const [selectedTemplate, setSelectedTemplate] = React.useState("");
  const [templateConfig, setTemplateConfig] = React.useState({
    startTime: "08:00",
    endTime: "13:00",
    days: 3,
  });

  const updateSessions = (nextSessions) => {
    const normalized = nextSessions.map((session, index) => ({
      ...session,
      sortOrder: index,
    }));
    onChange("sessions", normalized);
  };

  const updateSession = (index, patch) => {
    const next = sessions.map((session, sessionIndex) =>
      sessionIndex === index ? { ...session, ...patch } : session
    );
    updateSessions(next);
  };

  const addSession = () => {
    updateSessions([...sessions, createEmptySession()]);
  };

  const removeSession = (index) => {
    updateSessions(sessions.filter((_, sessionIndex) => sessionIndex !== index));
  };

  const moveSession = (index, direction) => {
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= sessions.length) return;
    const next = [...sessions];
    [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
    updateSessions(next);
  };

  const applyTemplate = () => {
    if (!selectedTemplate) return;
    const templateSessions = buildTemplateSessions({
      templateValue: selectedTemplate,
      eventDateBegin: formData.eventDateBegin,
      eventDateEnd: formData.eventDateEnd,
      templateConfig,
    });

    if (templateSessions.length === 0) return;
    const shouldReplace =
      sessions.length === 0 ||
      window.confirm("Sollen bestehende Sessions durch die Vorlage ersetzt werden?");
    if (!shouldReplace) return;

    updateSessions(templateSessions);
  };

  const selectedTemplateOption = TEMPLATE_OPTIONS.find(
    (entry) => entry.value === selectedTemplate
  );
  const hasEventDates =
    dayjs(formData.eventDateBegin).isValid() && dayjs(formData.eventDateEnd).isValid();
  const dailyPreview =
    selectedTemplate === "daily_sessions"
      ? getDailySessionsPreview(formData.eventDateBegin, formData.eventDateEnd)
      : null;

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6">Sessions</Typography>
        <Typography variant="body2" color="text.secondary">
          Erfassen Sie die einzelnen Veranstaltungstage oder Optionen (z.&nbsp;B. Zimmertypen,
          Pakete). Sessions können mit oder ohne Datum angelegt werden.
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems="flex-start">
            <TextField
              label="Vorlage anwenden"
              select
              size="small"
              value={selectedTemplate}
              onChange={(event) => setSelectedTemplate(event.target.value)}
              sx={{ minWidth: { xs: "100%", md: 320 } }}
              helperText={
                selectedTemplateOption
                  ? selectedTemplateOption.description
                  : "Optional: automatisch mehrere Sessions vorbefüllen"
              }
            >
              <MenuItem value="">Keine Vorlage</MenuItem>
              {TEMPLATE_OPTIONS.map((template) => (
                <MenuItem key={template.value} value={template.value}>
                  {template.label}
                </MenuItem>
              ))}
            </TextField>

            <Button
              variant="outlined"
              onClick={applyTemplate}
              disabled={!selectedTemplate}
              startIcon={<Iconify icon="solar:magic-stick-3-bold" width={18} />}
            >
              Vorlage anwenden
            </Button>
          </Stack>

          {/* Template-specific configuration */}
          <Collapse in={selectedTemplate === "daily_sessions"}>
            <Stack spacing={1.5}>
              {!hasEventDates && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Bitte zuerst Event-Start und Event-Ende im Schritt &quot;Basis&quot; festlegen.
                </Alert>
              )}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <TextField
                  label="Startzeit (täglich)"
                  type="time"
                  size="small"
                  value={templateConfig.startTime}
                  onChange={(e) =>
                    setTemplateConfig((prev) => ({ ...prev, startTime: e.target.value }))
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 160 }}
                />
                <TextField
                  label="Endzeit (täglich)"
                  type="time"
                  size="small"
                  value={templateConfig.endTime}
                  onChange={(e) =>
                    setTemplateConfig((prev) => ({ ...prev, endTime: e.target.value }))
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 160 }}
                />
              </Stack>
              {dailyPreview && !dailyPreview.error && (
                <Typography variant="body2" color="text.secondary">
                  Vorschau: {dailyPreview.count} Session{dailyPreview.count !== 1 ? "s" : ""} ({dailyPreview.range})
                </Typography>
              )}
              {dailyPreview?.error && (
                <Alert severity="error" sx={{ mt: 0.5 }}>
                  {dailyPreview.error}
                </Alert>
              )}
            </Stack>
          </Collapse>

          <Collapse in={selectedTemplate === "multi_day_camp"}>
            <Stack direction="row" spacing={1.5} sx={{ mt: 1 }}>
              <TextField
                label="Anzahl Tage"
                type="number"
                size="small"
                value={templateConfig.days}
                onChange={(e) =>
                  setTemplateConfig((prev) => ({
                    ...prev,
                    days: Math.max(1, Math.min(MAX_DAILY_SESSIONS, Number(e.target.value) || 1)),
                  }))
                }
                inputProps={{ min: 1, max: MAX_DAILY_SESSIONS }}
                sx={{ minWidth: 160 }}
              />
            </Stack>
          </Collapse>
        </Stack>
      </Paper>

      {errors.sessions ? <Alert severity="error">{errors.sessions}</Alert> : null}

      {sessions.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 2.5,
            borderStyle: "dashed",
            borderColor: "divider",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Es sind noch keine Sessions vorhanden.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={1.5}>
          {sessions.map((session, index) => {
            const sessionKey = session.sessionId || session._id || `session-${index}`;
            return (
              <Paper key={sessionKey} variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle2">Session {index + 1}</Typography>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="Nach oben">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => moveSession(index, "up")}
                            disabled={index === 0}
                          >
                            <Iconify icon="solar:arrow-up-bold" width={16} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Nach unten">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => moveSession(index, "down")}
                            disabled={index >= sessions.length - 1}
                          >
                            <Iconify icon="solar:arrow-down-bold" width={16} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Session entfernen">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeSession(index)}
                        >
                          <Iconify icon="solar:trash-bin-trash-bold" width={16} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>

                  <TextField
                    label="Bezeichnung"
                    value={session.label || ""}
                    onChange={(event) => updateSession(index, { label: event.target.value })}
                    required
                    fullWidth
                  />

                  <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                    <TextField
                      label="Beginn (optional)"
                      type="datetime-local"
                      value={toDateTimeLocal(session.dateStart)}
                      onChange={(event) =>
                        updateSession(index, { dateStart: event.target.value })
                      }
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                    <TextField
                      label="Ende (optional)"
                      type="datetime-local"
                      value={toDateTimeLocal(session.dateEnd)}
                      onChange={(event) =>
                        updateSession(index, { dateEnd: event.target.value })
                      }
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                    <TextField
                      label="Max. Teilnehmer"
                      type="number"
                      value={session.maxParticipants ?? ""}
                      onChange={(event) =>
                        updateSession(index, {
                          maxParticipants: event.target.value,
                        })
                      }
                      inputProps={{ min: 1 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">Plätze</InputAdornment>
                        ),
                      }}
                      sx={{ minWidth: { xs: "100%", md: 200 } }}
                    />
                  </Stack>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}

      <Box>
        <Button
          variant="outlined"
          startIcon={<Iconify icon="solar:add-circle-bold" width={18} />}
          onClick={addSession}
        >
          Session hinzufügen
        </Button>
      </Box>
    </Stack>
  );
}

StepSessions.propTypes = {
  formData: PropTypes.shape({
    eventDateBegin: PropTypes.string,
    eventDateEnd: PropTypes.string,
    sessions: PropTypes.arrayOf(
      PropTypes.shape({
        sessionId: PropTypes.string,
        _id: PropTypes.string,
        label: PropTypes.string,
        dateStart: PropTypes.string,
        dateEnd: PropTypes.string,
        maxParticipants: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        sortOrder: PropTypes.number,
      })
    ),
  }).isRequired,
  errors: PropTypes.shape({
    sessions: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
};
